import React, { useState, useEffect } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const AlphabetAdventureGame = ({ gameData, onComplete }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedLetters, setSelectedLetters] = useState([]);
    const [shuffledLetters, setShuffledLetters] = useState([]);
    const [feedback, setFeedback] = useState(null);
    const [score, setScore] = useState(0);

    useEffect(() => {
        if (gameData?.letters) {
            const shuffled = [...gameData.letters].sort(() => Math.random() - 0.5);
            setShuffledLetters(shuffled);
        }
    }, [gameData]);

    const handleLetterClick = (letter) => {
        if (feedback || selectedLetters.includes(letter.letter)) return;
        triggerHaptic(20);

        const expectedLetter = gameData.letters[currentIndex];

        if (letter.letter === expectedLetter.letter) {
            setFeedback('correct');
            setSelectedLetters(prev => [...prev, letter.letter]);
            const points = gameData.points_per_letter || 10;
            const newScore = score + points;
            setScore(newScore);
            triggerHaptic([100, 50, 100]);

            setTimeout(() => {
                if (currentIndex + 1 >= gameData.letters.length) {
                    onComplete?.(newScore);
                } else {
                    setCurrentIndex(prev => prev + 1);
                    setFeedback(null);
                }
            }, 1500);
        } else {
            setFeedback('wrong');
            triggerHaptic([50, 50, 50]);
            setTimeout(() => setFeedback(null), 1500);
        }
    };

    if (!gameData || !gameData.letters) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-sky-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">🔤</div>
                <h3 className="text-base md:text-3xl font-black text-sky-900 tracking-tighter uppercase">Ruošiamas abėcėlės nuotykis...</h3>
            </div>
        );
    }

    const currentLetter = gameData.letters[currentIndex];
    const progress = (currentIndex / gameData.letters.length) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-sky-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-sky-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-sky-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        🔤
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Abėcėlės Nuotykis
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Žinios</p>
                        <p className="text-sm md:text-2xl font-black text-sky-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Eiga</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentIndex + 1}/{gameData.letters.length}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-sky-400 via-indigo-500 to-purple-500 rounded-full transition-all duration-1000 shadow-[0_0_15px_rgba(56,189,248,0.3)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-8">
            {/* Target Card */}
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-6 md:p-12 text-center border-white/40 shadow-2xl bg-white/60 backdrop-blur-xl flex flex-col items-center justify-center relative overflow-hidden">
                <div className="absolute -top-4 -right-10 text-6xl md:text-[150px] opacity-[0.03] rotate-12 select-none pointer-events-none text-sky-900">✨</div>
                <div className="text-[8px] md:text-[10px] font-black text-sky-400 uppercase tracking-[0.5em] mb-4 md:mb-8">Tavo Taikinys</div>
                <div className="relative">
                    <div className="absolute -inset-10 bg-sky-200 rounded-full blur-3xl opacity-20 animate-pulse"></div>
                    <h2 className="text-8xl md:text-[14rem] font-black text-sky-600 leading-none drop-shadow-2xl relative z-10 animate-float">
                        {currentLetter.letter}
                    </h2>
                </div>
                <div className="mt-6 md:mt-12 bg-white rounded-2xl md:rounded-[3rem] p-4 md:p-8 border-2 md:border-4 border-sky-100 shadow-xl flex flex-col md:flex-row items-center gap-4 md:gap-6 max-w-2xl w-full">
                    <div className="text-6xl md:text-8xl drop-shadow-lg">{currentLetter.emoji}</div>
                    <div className="text-center md:text-left space-y-1">
                        <p className="text-lg md:text-4xl font-black text-slate-800 tracking-tighter uppercase">{currentLetter.word}</p>
                        <p className="text-sm md:text-xl font-bold text-slate-500 italic font-serif">"{currentLetter.hint}"</p>
                    </div>
                </div>
            </div>

            {/* Collected Progress Tray */}
            <div className="premium-glass rounded-2xl md:rounded-[3rem] p-4 md:p-8 shadow-xl border-white/40 bg-white/40 backdrop-blur-md overflow-x-auto no-scrollbar">
                <div className="flex items-center gap-2 mb-4">
                     <div className="w-1.5 h-1.5 bg-sky-500 rounded-full animate-pulse"></div>
                     <h4 className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Surinkta Abėcėlė</h4>
                </div>
                <div className="flex gap-2 md:gap-4 min-w-max">
                    {gameData.letters.map((letter, index) => (
                        <div
                            key={index}
                            className={`
                                w-10 h-10 md:w-16 md:h-16 rounded-xl md:rounded-2xl flex items-center justify-center text-sm md:text-2xl font-black transition-all duration-700 border-2 md:border-4
                                ${selectedLetters.includes(letter.letter)
                                    ? 'bg-emerald-500 text-white border-emerald-300 shadow-lg scale-100'
                                    : index === currentIndex
                                        ? 'bg-sky-500 text-white border-sky-300 animate-pulse scale-110 shadow-xl'
                                        : 'bg-white/50 text-slate-300 border-dashed border-slate-200'
                                }
                            `}
                        >
                            {letter.letter}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const targets = (
        <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-10 shadow-2xl border-white/60 bg-slate-900/90 relative overflow-hidden min-h-[300px]">
            <div className="absolute top-0 right-0 p-4 text-6xl opacity-[0.05] select-none pointer-events-none text-white">🔍</div>
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2 md:gap-4 relative z-10">
                {shuffledLetters.map((letter, index) => {
                    const isSelected = selectedLetters.includes(letter.letter);
                    const isCurrent = letter.letter === currentLetter.letter;

                    return (
                        <button
                            key={index}
                            onClick={() => handleLetterClick(letter)}
                            disabled={isSelected || !!feedback}
                            className={`
                                relative aspect-square rounded-xl md:rounded-[2rem] text-xl md:text-4xl font-black transition-all duration-500 transform border-b-4 md:border-b-8
                                ${isSelected
                                    ? 'bg-emerald-500 text-white border-emerald-700 shadow-inner scale-95 opacity-40'
                                    : feedback === 'correct' && isCurrent
                                        ? 'bg-sky-500 text-white border-sky-700 scale-110 shadow-[0_0_30px_rgba(56,189,248,0.5)]'
                                        : feedback === 'wrong' && isCurrent
                                            ? 'bg-rose-500 text-white border-rose-700 animate-shake'
                                            : 'bg-white text-slate-800 border-slate-200 hover:bg-sky-50 active:translate-y-1 active:border-b-0 shadow-lg'
                                }
                                disabled:cursor-default
                            `}
                        >
                            <span className="relative z-10">{isSelected ? '✓' : letter.letter}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 pointer-events-none flex items-center justify-center z-[2000] p-4">
                    <div className={`
                        px-8 py-6 md:px-16 md:py-12 rounded-[2.5rem] md:rounded-[4rem] shadow-2xl backdrop-blur-2xl border-4 md:border-8 animate-pop-in text-center max-w-sm
                        ${feedback === 'correct' ? 'bg-emerald-500/90 border-emerald-400 text-white' : 'bg-rose-500/90 border-rose-400 text-white'}
                    `}>
                        <div className="text-7xl md:text-[120px] mb-4 animate-bounce">
                            {feedback === 'correct' ? '🌟' : '🧐'}
                        </div>
                        <p className="text-xl md:text-4xl font-black tracking-tight uppercase">
                            {feedback === 'correct' ? 'Rasta!' : 'Ieškok geriau!'}
                        </p>
                    </div>
                </div>
            )}
        </>
    );
};

export default AlphabetAdventureGame;
